/**
 * InOut's words, as a person reads them: each value the engine (or the record)
 * answers with, mapped to its translation key. Every map is complete by
 * construction (`satisfies Record<…>`), so a value the engine adds fails the
 * typecheck here instead of rendering a raw id; and every key is guarded by
 * packages/locale/src/dynamic-key-coverage.spec.ts. One home for both citizen
 * apps.
 *
 * Presentation only — the numbers and the verdicts are the engine's.
 */
import type { DurationUnit } from './duration';
import type { InOutOfferProduct, InOutOfferTone } from './offer';
import type { InOutClockFigures } from './presentation';
import type {
  Accrual,
  Consequence,
  Direction,
  Geography,
  InOutAcquisitionStatus,
  InOutAwayGeography,
  InOutClock,
  InOutClockStatus,
  InOutConfirmationCadence,
  InOutEvaluation,
} from './types';
import type { TrackingFileProblem } from './tracking-file';

/**
 * A clock's name: what is counted, how, and where — `{{country}}` is the host
 * country's name. Two rules never share a name: the days away from the host
 * and the days in the other Schengen countries are both totals, and read as
 * two different things.
 */
export const INOUT_CLOCK_NAME_KEY = {
  MAX_ABSENCE: {
    CUMULATIVE: {
      OUTSIDE_HOST: 'inout.clock.name.awayFromHost',
      OUTSIDE_EU: 'inout.clock.name.outsideEu',
      INSIDE_HOST: 'inout.clock.name.countedInHost',
      SCHENGEN_EXCLUDING_HOST: 'inout.clock.name.otherSchengen',
    },
    CONSECUTIVE: {
      OUTSIDE_HOST: 'inout.clock.name.eachTripFromHost',
      OUTSIDE_EU: 'inout.clock.name.eachTripOutsideEu',
      INSIDE_HOST: 'inout.clock.name.eachStayInHost',
      SCHENGEN_EXCLUDING_HOST: 'inout.clock.name.eachStayOtherSchengen',
    },
  },
  MIN_PRESENCE: {
    CUMULATIVE: {
      OUTSIDE_HOST: 'inout.clock.name.neededOutsideHost',
      OUTSIDE_EU: 'inout.clock.name.neededOutsideEu',
      INSIDE_HOST: 'inout.clock.name.neededInHost',
      SCHENGEN_EXCLUDING_HOST: 'inout.clock.name.neededOtherSchengen',
    },
    CONSECUTIVE: {
      OUTSIDE_HOST: 'inout.clock.name.unbrokenOutsideHost',
      OUTSIDE_EU: 'inout.clock.name.unbrokenOutsideEu',
      INSIDE_HOST: 'inout.clock.name.unbrokenInHost',
      SCHENGEN_EXCLUDING_HOST: 'inout.clock.name.unbrokenOtherSchengen',
    },
  },
} as const satisfies Record<Direction, Record<Accrual, Record<Geography, string>>>;

/**
 * A clock's short name — a tab's label where every rule of the person's sits
 * in one row (the phone's limits, owner 20 Oct 2026); the full name
 * (`INOUT_CLOCK_NAME_KEY`) heads the rule itself and is what a screen reader
 * says. No `{{country}}`: a tab is a word or two.
 */
export const INOUT_CLOCK_SHORT_NAME_KEY = {
  MAX_ABSENCE: {
    CUMULATIVE: {
      OUTSIDE_HOST: 'inout.clock.short.awayFromHost',
      OUTSIDE_EU: 'inout.clock.short.outsideEu',
      INSIDE_HOST: 'inout.clock.short.countedInHost',
      SCHENGEN_EXCLUDING_HOST: 'inout.clock.short.otherSchengen',
    },
    CONSECUTIVE: {
      OUTSIDE_HOST: 'inout.clock.short.eachTripFromHost',
      OUTSIDE_EU: 'inout.clock.short.eachTripOutsideEu',
      INSIDE_HOST: 'inout.clock.short.eachStayInHost',
      SCHENGEN_EXCLUDING_HOST: 'inout.clock.short.eachStayOtherSchengen',
    },
  },
  MIN_PRESENCE: {
    CUMULATIVE: {
      OUTSIDE_HOST: 'inout.clock.short.neededOutsideHost',
      OUTSIDE_EU: 'inout.clock.short.neededOutsideEu',
      INSIDE_HOST: 'inout.clock.short.neededInHost',
      SCHENGEN_EXCLUDING_HOST: 'inout.clock.short.neededOtherSchengen',
    },
    CONSECUTIVE: {
      OUTSIDE_HOST: 'inout.clock.short.unbrokenOutsideHost',
      OUTSIDE_EU: 'inout.clock.short.unbrokenOutsideEu',
      INSIDE_HOST: 'inout.clock.short.unbrokenInHost',
      SCHENGEN_EXCLUDING_HOST: 'inout.clock.short.unbrokenOtherSchengen',
    },
  },
} as const satisfies Record<Direction, Record<Accrual, Record<Geography, string>>>;

/** A clock's short name — a tab's label. */
export function inoutClockShortNameKey(
  clock: Pick<InOutClock, 'direction' | 'accrual' | 'geography'>
): string {
  return INOUT_CLOCK_SHORT_NAME_KEY[clock.direction][clock.accrual][clock.geography];
}

/** The name of a clock (`t(key, { country })`). */
export function inoutClockNameKey(
  clock: Pick<InOutClock, 'direction' | 'accrual' | 'geography'>
): string {
  return INOUT_CLOCK_NAME_KEY[clock.direction][clock.accrual][clock.geography];
}

/**
 * The window a limit is written for, as its words name it: none; the
 * permit's validity (`TITLE_VALIDITY` that a renewal resets); the permit and
 * its renewals (one that runs across them); any span of the same size
 * (`ROLLING`); a calendar year; the first span from the title's issue.
 */
export type InOutLimitWindowKind =
  'none' | 'permit' | 'permitAndRenewals' | 'rolling' | 'calendarYear' | 'fromIssue';

/**
 * A limit on absence as a meter names it (owner, 22 Oct 2026 — "No total",
 * "Seguidos"): how it counts, and outside the EU when that is where it counts.
 */
export const INOUT_METER_NAME_KEY = {
  OUTSIDE_HOST: {
    CUMULATIVE: 'inout.meter.name.inTotal',
    CONSECUTIVE: 'inout.meter.name.inARow',
  },
  OUTSIDE_EU: {
    CUMULATIVE: 'inout.meter.name.outsideEuInTotal',
    CONSECUTIVE: 'inout.meter.name.outsideEuInARow',
  },
} as const satisfies Record<InOutAwayGeography, Record<Accrual, string>>;

/**
 * The line under a meter's name — the limit as the law writes it, short:
 * "8 months in the permit", "6 months per trip" (`{{limit}}`, `{{window}}`).
 */
export const INOUT_METER_DETAIL_KEY = {
  CONSECUTIVE: {
    none: 'inout.meter.detail.perTrip',
    permit: 'inout.meter.detail.perTrip',
    permitAndRenewals: 'inout.meter.detail.perTrip',
    rolling: 'inout.meter.detail.perTripRolling',
    calendarYear: 'inout.meter.detail.perTripCalendarYear',
    fromIssue: 'inout.meter.detail.perTripFromIssue',
  },
  CUMULATIVE: {
    none: 'inout.meter.detail.total',
    permit: 'inout.meter.detail.totalPermit',
    permitAndRenewals: 'inout.meter.detail.totalPermitAndRenewals',
    rolling: 'inout.meter.detail.totalRolling',
    calendarYear: 'inout.meter.detail.totalCalendarYear',
    fromIssue: 'inout.meter.detail.totalFromIssue',
  },
} as const satisfies Record<Accrual, Record<InOutLimitWindowKind, string>>;

/** A duration as the law writes it — "6 months", "90 days", "6 years" (`{{value}}`, by `count`). */
export const INOUT_DURATION_KEY = {
  days: 'inout.duration.days',
  months: 'inout.duration.months',
  years: 'inout.duration.years',
} as const satisfies Record<DurationUnit, string>;

/** What the rule says can follow once the limit is passed. */
export const INOUT_CONSEQUENCE_KEY = {
  MAY_CANCEL: 'inout.clock.consequence.mayCancel',
  LOSES_STATUS: 'inout.clock.consequence.losesStatus',
  BLOCKS_ACQUISITION: 'inout.clock.consequence.blocksAcquisition',
  IRREGULAR_STAY_HOST_MS: 'inout.clock.consequence.irregularStay',
} as const satisfies Record<Consequence, string>;

/** A clock's state, in words (AC-2: an unresolved rule has words, never a number). */
export const INOUT_CLOCK_STATUS_KEY = {
  WITHIN: 'inout.clock.status.within',
  DEPENDS_ON_UNCERTAIN_DAYS: 'inout.clock.status.depends',
  BEYOND: 'inout.clock.status.beyond',
  UNRESOLVED: 'inout.clock.status.unresolved',
  NOT_APPLICABLE: 'inout.clock.status.notApplicable',
} as const satisfies Record<InOutClockStatus, string>;

/** Who settles an unresolved rule — the remedy is theirs, not the person's (unless `USER`). */
export const INOUT_UNRESOLVED_OWNER_KEY = {
  AGENCY: 'inout.clock.owner.agency',
  TONNIE: 'inout.clock.owner.tonnie',
  USER: 'inout.clock.owner.user',
} as const satisfies Record<NonNullable<InOutClock['unresolvedOwner']>, string>;

/** Why the rule that binds first is the one to plan by. */
export const INOUT_BINDING_REASON_KEY = {
  ALREADY_REACHED: 'inout.binding.alreadyReached',
  MAY_BE_REACHED: 'inout.binding.mayBeReached',
  EARLIEST_EXHAUSTION: 'inout.binding.earliestExhaustion',
  LEAST_REMAINING: 'inout.binding.leastRemaining',
} as const satisfies Record<NonNullable<InOutEvaluation['binding']>['reason'], string>;

/** A long-term right's clock (acquisition), in words. */
export const INOUT_ACQUISITION_STATUS_KEY = {
  ACCRUING: 'inout.acquisition.status.accruing',
  COMPLETE: 'inout.acquisition.status.complete',
  DEPENDS_ON_UNCERTAIN_DAYS: 'inout.acquisition.status.depends',
  TOLERANCE_EXCEEDED: 'inout.acquisition.status.toleranceExceeded',
  UNRESOLVED: 'inout.acquisition.status.unresolved',
  NOT_APPLICABLE: 'inout.acquisition.status.notApplicable',
} as const satisfies Record<InOutAcquisitionStatus, string>;

/** How often the person is asked where they were. */
export const INOUT_CADENCE_KEY = {
  P15D: 'inout.notices.cadence.P15D',
  P30D: 'inout.notices.cadence.P30D',
  P1M: 'inout.notices.cadence.P1M',
  OFF: 'inout.notices.cadence.OFF',
} as const satisfies Record<InOutConfirmationCadence, string>;

/** When an exception the law names has to be asked for. */
export const INOUT_EXCEPTION_TIMING_KEY = {
  BEFORE_DEPARTURE: 'inout.exceptions.timing.beforeDeparture',
  ANY: 'inout.exceptions.timing.any',
} as const satisfies Record<'BEFORE_DEPARTURE' | 'ANY', string>;

/** A presence confirmation's state — open (no answer yet), answered, or closed unanswered. */
export const INOUT_CONFIRMATION_STATE_KEY = {
  OPEN: 'inout.notices.confirmation.state.open',
  IN_HOST: 'inout.notices.confirmation.state.inHost',
  WAS_AWAY: 'inout.notices.confirmation.state.wasAway',
  NO_ANSWER: 'inout.notices.confirmation.state.noAnswer',
} as const satisfies Record<'OPEN' | 'IN_HOST' | 'WAS_AWAY' | 'NO_ANSWER', string>;

// ─── The case with the agency (Lane 8) — both citizen apps and the operation ──

/** Why the person shares their record with the agency. */
export const INOUT_SHARING_PURPOSE_KEY = {
  CONSULTATION: 'inout.agency.purpose.consultation',
  REQUISITION: 'inout.agency.purpose.requisition',
} as const satisfies Record<'CONSULTATION' | 'REQUISITION', string>;

/** What a purpose shares with the agency — the country rules' closed list. */
export const INOUT_SHARED_FIELD_KEY = {
  IDENTITY: 'inout.agency.field.identity',
  CONTACT: 'inout.agency.field.contact',
  TITLE: 'inout.agency.field.title',
  ABSENCES: 'inout.agency.field.absences',
  PLANNED_TRIPS: 'inout.agency.field.plannedTrips',
  CLOCKS: 'inout.agency.field.clocks',
  EVIDENCE_DOCUMENTS: 'inout.agency.field.evidenceDocuments',
} as const satisfies Record<
  'IDENTITY' | 'CONTACT' | 'TITLE' | 'ABSENCES' | 'PLANNED_TRIPS' | 'CLOCKS' | 'EVIDENCE_DOCUMENTS',
  string
>;

/** A case's state. */
export const INOUT_CASE_STATE_KEY = {
  open: 'inout.case.state.open',
  in_progress: 'inout.case.state.inProgress',
  closed: 'inout.case.state.closed',
} as const satisfies Record<'open' | 'in_progress' | 'closed', string>;

/** Why a case was closed (the agency's tracking). */
export const INOUT_CASE_CLOSED_REASON_KEY = {
  COMPLETED: 'inout.case.closedReason.completed',
  WITHOUT_SUBMISSION: 'inout.case.closedReason.withoutSubmission',
} as const satisfies Record<'COMPLETED' | 'WITHOUT_SUBMISSION', string>;

/** Where a consultation is. */
export const INOUT_CONSULTATION_STATE_KEY = {
  pending_payment: 'inout.consultation.state.pendingPayment',
  paid: 'inout.consultation.state.paid',
  scheduled: 'inout.consultation.state.scheduled',
  completed: 'inout.consultation.state.completed',
  payment_received_rebook: 'inout.consultation.state.paymentReceivedRebook',
  cancelled_by_agency: 'inout.consultation.state.cancelledByAgency',
  checkout_expired: 'inout.consultation.state.checkoutExpired',
} as const satisfies Record<
  | 'pending_payment'
  | 'paid'
  | 'scheduled'
  | 'completed'
  | 'payment_received_rebook'
  | 'cancelled_by_agency'
  | 'checkout_expired',
  string
>;

/** Where a requisition is. */
export const INOUT_REQUISITION_STATE_KEY = {
  pending_payment: 'inout.requisition.state.pendingPayment',
  paid: 'inout.requisition.state.paid',
  delivered: 'inout.requisition.state.delivered',
  in_progress: 'inout.requisition.state.inProgress',
  completed: 'inout.requisition.state.completed',
  closed_without_submission: 'inout.requisition.state.closedWithoutSubmission',
  checkout_expired: 'inout.requisition.state.checkoutExpired',
} as const satisfies Record<
  | 'pending_payment'
  | 'paid'
  | 'delivered'
  | 'in_progress'
  | 'completed'
  | 'closed_without_submission'
  | 'checkout_expired',
  string
>;

/** A refund request's state. */
export const INOUT_REFUND_REQUEST_STATE_KEY = {
  requested: 'inout.refundRequest.state.requested',
  handled_externally: 'inout.refundRequest.state.handledExternally',
} as const satisfies Record<'requested' | 'handled_externally', string>;

/** Where an agency event came from. */
export const INOUT_CASE_EVENT_SOURCE_KEY = {
  AGENCY_FILE: 'inout.operations.eventSource.agencyFile',
  OPERATOR: 'inout.operations.eventSource.operator',
} as const satisfies Record<'AGENCY_FILE' | 'OPERATOR', string>;

/** A tracking file's state. */
export const INOUT_TRACKING_IMPORT_STATE_KEY = {
  previewed: 'inout.operations.files.state.previewed',
  accepted: 'inout.operations.files.state.accepted',
  rejected: 'inout.operations.files.state.rejected',
  expired: 'inout.operations.files.state.expired',
} as const satisfies Record<'previewed' | 'accepted' | 'rejected' | 'expired', string>;

/** What becomes of a file's row. */
export const INOUT_TRACKING_ROW_OUTCOME_KEY = {
  new: 'inout.operations.files.outcome.new',
  duplicate: 'inout.operations.files.outcome.duplicate',
  error: 'inout.operations.files.outcome.error',
} as const satisfies Record<'new' | 'duplicate' | 'error', string>;

/** What can be wrong with a tracking file or one of its rows — the reader's and the API's. */
export type InOutTrackingRowProblem = TrackingFileProblem | 'CASE_UNKNOWN' | 'CASE_CLOSED';

export const INOUT_TRACKING_PROBLEM_KEY = {
  EMPTY_FILE: 'inout.operations.problem.emptyFile',
  HEADER_MISMATCH: 'inout.operations.problem.headerMismatch',
  COLUMN_COUNT: 'inout.operations.problem.columnCount',
  UNCLOSED_QUOTE: 'inout.operations.problem.unclosedQuote',
  FORMAT_VERSION: 'inout.operations.problem.formatVersion',
  AGENCY_MISMATCH: 'inout.operations.problem.agencyMismatch',
  EVENT_ID_MISSING: 'inout.operations.problem.eventIdMissing',
  EVENT_ID_REPEATED: 'inout.operations.problem.eventIdRepeated',
  CASE_REF_MISSING: 'inout.operations.problem.caseRefMissing',
  EVENT_CODE_UNKNOWN: 'inout.operations.problem.eventCodeUnknown',
  NOTE_CODE_UNKNOWN: 'inout.operations.problem.noteCodeUnknown',
  OCCURRED_AT_INVALID: 'inout.operations.problem.occurredAtInvalid',
  CASE_UNKNOWN: 'inout.operations.problem.caseUnknown',
  CASE_CLOSED: 'inout.operations.problem.caseClosed',
} as const satisfies Record<InOutTrackingRowProblem, string>;

/** The overview's offer: its title, for how pressing it is (owner, 19 Oct 2026). */
export const INOUT_OFFER_TITLE_KEY = {
  calm: 'inout.offer.title.calm',
  review: 'inout.offer.title.review',
  urgent: 'inout.offer.title.urgent',
} as const satisfies Record<InOutOfferTone, string>;

/** …and the line under it. */
export const INOUT_OFFER_BODY_KEY = {
  calm: 'inout.offer.body.calm',
  review: 'inout.offer.body.review',
  urgent: 'inout.offer.body.urgent',
} as const satisfies Record<InOutOfferTone, string>;

/** …and the action that takes it. */
export const INOUT_OFFER_ACTION_KEY = {
  consultation: 'inout.agency.offer.bookConsultation',
  requisition: 'inout.agency.offer.buyRequisition',
} as const satisfies Record<InOutOfferProduct, string>;

/** What a rule's large figure counts (the overview's line, the limits' card): days, days in a row, days still needed. */
export const INOUT_CLOCK_DAYS_UNIT_KEY = {
  total: 'inout.summary.unit.total',
  eachTrip: 'inout.summary.unit.eachTrip',
  needed: 'inout.summary.unit.needed',
} as const satisfies Record<InOutClockFigures['kind'], string>;

/**
 * Whether a clock carries a number to show. An unresolved rule never does, on
 * any surface (AC-2) — whatever its fields hold.
 */
export function inoutClockHasNumber(
  clock: InOutClock
): clock is InOutClock & { value: number; limit: number } {
  return clock.status !== 'UNRESOLVED' && clock.value !== null && clock.limit !== null;
}
